/**
 * CommunityGrid Component
 * 2-column responsive grid of community cards with infinite scroll
 */

import { useNavigate } from "react-router-dom"

import { formatLocation } from "@/components/shared/formatLocation"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import type { ConnectCommunity } from "../types/connect.types"

interface CommunityGridProps {
  communities: ConnectCommunity[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
}

function ExploreCard({ community }: { community: ConnectCommunity }) {
  const navigate = useNavigate()
  const location = formatLocation(community.city, community.state)
  const coverImage = community.coverImage ?? ""

  const handleClick = () => {
    navigate(`/communities/${community.id}`)
  }

  return (
    <div
      className="relative aspect-[16/11] rounded-[20px] overflow-hidden group cursor-pointer"
      onClick={handleClick}
    >
      {/* Cover Image */}
      {coverImage ? (
        <img
          src={coverImage}
          alt={community.name}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-card" />
      )}

      {/* Bottom Gradient Overlay */}
      <div className="absolute inset-x-0 -bottom-2.5  bg-gradient-to-t from-black/80 to-transparent backdrop-blur-sm mb-4">
        <div className="absolute inset-x-0 bottom-0 px-5 pb-4 flex flex-col">
          {/* Name */}
          <span className="text-[18px] font-semibold text-text-primary opacity-90 truncate">
            {community.name}
          </span>

          {/* Members + Rituals */}
          <div className="flex items-center gap-[10px] text-[13px] text-text-primary opacity-70 mt-1">
            <span>{community.memberCount} Members</span>
            <span>{community.ritualCount} Rituals</span>
          </div>

          {/* Location */}
          {location && (
            <span className="text-[13px] text-text-primary opacity-70 truncate mt-2">
              {location}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function CommunityGrid({ communities, isLoading, hasMore, onLoadMore }: CommunityGridProps) {
  const loadMoreRef = useInfiniteScroll({
    onLoadMore,
    hasMore,
    threshold: 200,
  })

  return (
    <div className="mt-6">
      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {communities.map(community => (
          <ExploreCard key={community.id} community={community} />
        ))}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="aspect-[16/11] bg-card rounded-[20px] animate-pulse" aria-hidden="true" />
          <div className="aspect-[16/11] bg-card rounded-[20px] animate-pulse" aria-hidden="true" />
        </div>
      )}

      {/* Sentinel for infinite scroll */}
      {(hasMore || isLoading) && <div ref={loadMoreRef} className="h-4" aria-hidden="true" />}
    </div>
  )
}
